import { format, parseISO } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import type { CandidateWorkExperienceResponseApi } from '@/modules/candidates/types';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';

const MAX_EXPERIENCES_TO_DISPLAY = 5;

interface WorkExperienceTimelineProps {
  workExperiences: CandidateWorkExperienceResponseApi[];
  expandable?: boolean;
  defaultExpanded?: boolean;
}

export const WorkExperienceTimeline = ({
  workExperiences,
  expandable = true,
  defaultExpanded = false,
}: WorkExperienceTimelineProps) => {
  const [showAll, setShowAll] = useState(defaultExpanded);

  const displayedExperiences =
    showAll || !expandable ? workExperiences : workExperiences.slice(0, 5);

  return (
    <div className="flex flex-col gap-4">
      <div>
        {displayedExperiences.map((experience, index) => (
          <TimelineItem
            key={experience.id}
            experience={experience}
            isLast={index === displayedExperiences.length - 1}
          />
        ))}
      </div>

      {workExperiences.length > MAX_EXPERIENCES_TO_DISPLAY && expandable && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="hover:text-primary text-primary flex w-fit cursor-pointer items-center gap-1 text-xs transition-colors"
        >
          {showAll ? 'Show less' : 'Show more'}
          <ChevronRight
            className={cn(
              'size-4 transition-transform',
              showAll && 'rotate-270',
            )}
          />
        </button>
      )}
    </div>
  );
};

const isCareerBreak = (exp: CandidateWorkExperienceResponseApi) => {
  return (
    exp.jobTitle?.toLowerCase().includes('career break') ||
    exp.jobTitle?.toLowerCase().includes('gap') ||
    exp.companyName?.toLowerCase().includes('travel') ||
    exp.companyName?.toLowerCase().includes('sabbatical')
  );
};

const formatDate = (date: string) => {
  return format(parseISO(date), 'MMMM yyyy');
};

interface TimelineItemProps {
  experience: CandidateWorkExperienceResponseApi;
  isLast: boolean;
}

const TimelineItem = ({ experience, isLast }: TimelineItemProps) => {
  const careerBreak = isCareerBreak(experience);
  const isCurrentJob = !experience.endDate;

  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'mt-1.5 size-3 shrink-0 rounded-full border-2',
            careerBreak
              ? 'border-neutral-400 bg-neutral-400'
              : 'border-primary bg-white',
          )}
        />
        {!isLast && <div className="min-h-8 w-0.5 flex-1 bg-neutral-300" />}
      </div>

      <div className="flex-1 pb-4">
        <Subheading
          variant="h6"
          className={cn(careerBreak ? 'text-neutral-400' : 'text-primary')}
        >
          {experience.jobTitle}
        </Subheading>

        <p className="text-neutral-650 mt-0.5 text-xs">
          {experience.startDate
            ? formatDate(experience.startDate)
            : 'Unspecified'}
          {' - '}

          {isCurrentJob
            ? 'Now'
            : experience.endDate
              ? formatDate(experience.endDate)
              : 'Present'}

          {experience.companyName && (
            <>
              {' | '}
              {experience.companyName}
              {experience.location && `, ${experience.location}`}
            </>
          )}
        </p>
      </div>
    </div>
  );
};
